/**
 * 4D Look-Ahead — the look-ahead report, driven straight off the 4D mappings so
 * every row owns its 3D objects.
 *
 * Three levels of interaction with the model:
 *   row click      → isolate + zoom that activity's geometry
 *   window paint   → the whole horizon colour-coded on the model by bucket
 *   play / export  → animate just this window, then hand the dates to the
 *                    video exporter so the clip covers exactly the horizon.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { authFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Calendar, RefreshCw, Loader2, ChevronRight, ChevronDown, Play, Pause,
  Film, Palette, Eraser, X, Layers,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BUCKETS = [
  { key: 'UPCOMING', label: 'UPCOMING', color: '#38BDF8', text: 'text-sky-500' },
  { key: 'IN_PROGRESS', label: 'IN PROGRESS', color: '#F59E0B', text: 'text-amber-500' },
  { key: 'FINISHING', label: 'FINISHING', color: '#22C55E', text: 'text-emerald-500' },
  { key: 'AT_RISK', label: 'AT RISK', color: '#EF4444', text: 'text-red-500' },
];

const WINDOWS = [1, 2, 3, 4, 6, 8, 12, 16, 24, 52];
const d10 = (v) => (v ? String(v).slice(0, 10) : '—');

export function FourDLookAhead({ projectId, modelId, viewerRef, onClose, onExportClip }) {
  const base = `${API}/api/v1/projects/${projectId}/4d/models/${modelId}`;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [windowWeeks, setWindowWeeks] = useState(12);
  const [basis, setBasis] = useState('FORECAST');
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [linkedOnly, setLinkedOnly] = useState(false);
  const [discipline, setDiscipline] = useState('');
  const [search, setSearch] = useState('');
  const [activeBucket, setActiveBucket] = useState(null);
  const [treeMode, setTreeMode] = useState(true);
  const [collapsed, setCollapsed] = useState(new Set());
  const [selectedCode, setSelectedCode] = useState(null);
  const [painted, setPainted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [playWeek, setPlayWeek] = useState(0);
  const playRef = useRef(null);

  const load = useCallback(async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        windowWeeks: String(windowWeeks), basis,
        criticalOnly: String(criticalOnly), linkedOnly: String(linkedOnly),
      });
      if (discipline.trim()) qs.set('discipline', discipline.trim());
      if (search.trim()) qs.set('search', search.trim());
      const r = await authFetch(`${base}/lookahead?${qs}`);
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || 'Look-ahead failed'); return; }
      setData(d);
    } catch { toast.error('Look-ahead failed'); } finally { setLoading(false); }
  }, [base, modelId, windowWeeks, basis, criticalOnly, linkedOnly, discipline, search]);

  useEffect(() => { load(); }, [load]);

  const leafRows = useMemo(() => (data?.rows || []).filter(r => r.bucket), [data]);
  const visibleRows = useMemo(() => {
    if (!activeBucket) return data?.rows || [];
    const keep = new Set();
    for (const r of leafRows) {
      if (r.bucket !== activeBucket) continue;
      keep.add(r.activityCode);
      const parts = (r.activityCode || '').split('.');
      for (let i = 1; i < parts.length; i++) keep.add(parts.slice(0, i).join('.'));
    }
    return (data?.rows || []).filter(r => keep.has(r.activityCode));
  }, [data, activeBucket, leafRows]);

  // Tree rendering is a flat list filtered by collapse state — the WBS code
  // already encodes the hierarchy, so no nesting pass is needed.
  const rendered = useMemo(() => {
    if (!treeMode) return visibleRows.filter(r => r.bucket);
    const hasChildren = new Set();
    for (const r of visibleRows) {
      const parts = (r.activityCode || '').split('.');
      for (let i = 1; i < parts.length; i++) hasChildren.add(parts.slice(0, i).join('.'));
    }
    return visibleRows.filter(r => {
      const parts = (r.activityCode || '').split('.');
      for (let i = 1; i < parts.length; i++) {
        if (collapsed.has(parts.slice(0, i).join('.'))) return false;
      }
      return true;
    }).map(r => ({ ...r, _hasChildren: hasChildren.has(r.activityCode),
                   _level: (r.activityCode || '').split('.').length - 1 }));
  }, [visibleRows, treeMode, collapsed]);

  const toggle = (code) => setCollapsed(prev => {
    const n = new Set(prev);
    n.has(code) ? n.delete(code) : n.add(code);
    return n;
  });

  // ── 3D interaction ───────────────────────────────────────────────────
  const pickRow = (row) => {
    setSelectedCode(row.activityCode);
    if (!row.dbIds?.length) {
      toast.info(`${row.activityCode} has no linked 3D objects yet.`);
      return;
    }
    const ok = viewerRef?.current?.applyHotspot?.(
      row.dbIds.map(dbId => ({ dbId, color: row.color || '#38BDF8' })));
    if (!ok) toast.error('The 3D viewer is not ready yet.');
  };

  const paintWindow = useCallback((upToWeek = null) => {
    const objs = [];
    const dd = data?.dataDate ? new Date(data.dataDate) : null;
    for (const r of leafRows) {
      if (!r.dbIds?.length) continue;
      if (upToWeek != null && dd) {
        // During playback only the activities whose window-start has arrived
        // are painted, so the horizon fills in week by week.
        const start = r.aStart || (basis === 'BASELINE' ? r.bStart : r.fStart);
        if (start) {
          const weeks = Math.floor((new Date(start) - dd) / (7 * 864e5));
          if (weeks > upToWeek) continue;
        }
      }
      for (const dbId of r.dbIds) objs.push({ dbId, color: r.color, opacity: 0.95 });
    }
    if (!objs.length) { toast.info('No linked geometry in this window.'); return false; }
    const ok = viewerRef?.current?.paintFrame?.(objs, { ghostRest: true, isolate: false });
    if (!ok) { toast.error('The 3D viewer is not ready yet.'); return false; }
    setPainted(true);
    return true;
  }, [leafRows, viewerRef, data, basis]);

  const clearPaint = () => {
    viewerRef?.current?.clearHotspot?.();
    setPainted(false);
    setSelectedCode(null);
  };

  useEffect(() => {
    if (!playing) { clearInterval(playRef.current); return; }
    playRef.current = setInterval(() => {
      setPlayWeek(w => {
        if (w >= windowWeeks) { setPlaying(false); return windowWeeks; }
        return w + 1;
      });
    }, 700);
    return () => clearInterval(playRef.current);
  }, [playing, windowWeeks]);

  useEffect(() => { if (playing) paintWindow(playWeek); }, [playWeek, playing, paintWindow]);

  useEffect(() => () => clearInterval(playRef.current), []);

  const counts = data?.counts || {};

  return (
    <div className="flex flex-col h-full bg-background" data-testid="fourd-lookahead-panel">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b">
        <Calendar className="w-4 h-4 text-sky-500" />
        <div className="min-w-0">
          <h3 className="text-sm font-semibold leading-tight">Look-Ahead Report</h3>
          <p className="text-[10px] text-muted-foreground">
            Horizon: {d10(data?.dataDate)} → {d10(data?.horizon)} · {data?.totalRows ?? 0} activities in window
          </p>
        </div>

        <div className="ml-auto flex items-center gap-1.5">
          <Button size="sm" variant={painted ? 'default' : 'outline'}
                  className={`h-7 text-xs gap-1 ${painted ? 'bg-sky-600 hover:bg-sky-700' : ''}`}
                  onClick={() => (painted ? clearPaint() : paintWindow())}
                  data-testid="lookahead-paint-window">
            {painted ? <Eraser className="w-3 h-3" /> : <Palette className="w-3 h-3" />}
            {painted ? 'Clear model' : 'Paint window on model'}
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
                  onClick={() => { setPlayWeek(0); setPlaying(p => !p); }}
                  data-testid="lookahead-play">
            {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            {playing ? `Week +${playWeek}` : 'Play the look-ahead'}
          </Button>
          <Button size="sm" className="h-7 text-xs gap-1 bg-orange-600 hover:bg-orange-700"
                  onClick={() => onExportClip?.({
                    fromDate: d10(data?.dataDate), toDate: d10(data?.horizon),
                    basis: basis === 'BASELINE' ? 'PLAN' : 'FORECAST',
                  })}
                  data-testid="lookahead-export-clip">
            <Film className="w-3 h-3" /> Export this window
          </Button>
          <Select value={String(windowWeeks)} onValueChange={v => setWindowWeeks(Number(v))}>
            <SelectTrigger className="h-7 w-24 text-xs" data-testid="lookahead-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map(n => (
                <SelectItem key={n} value={String(n)}>{n === 52 ? '1 yr' : `${n} wks`}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={load} disabled={loading}
                  data-testid="lookahead-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onClose}
                  data-testid="lookahead-close">
            <X className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b text-[11px]">
        <Select value={basis} onValueChange={setBasis}>
          <SelectTrigger className="h-6 w-36 text-[11px]" data-testid="lookahead-basis">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="FORECAST">Forecast dates</SelectItem>
            <SelectItem value="BASELINE">Baseline dates</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={() => setCriticalOnly(v => !v)}
                className={`px-2 py-1 rounded-full border ${criticalOnly ? 'bg-red-100 text-red-700 border-red-300 dark:bg-red-500/20 dark:text-red-300' : 'text-muted-foreground border-border'}`}
                data-testid="lookahead-filter-cp">CP only</button>
        <button onClick={() => setLinkedOnly(v => !v)}
                className={`px-2 py-1 rounded-full border ${linkedOnly ? 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300 dark:bg-fuchsia-500/20 dark:text-fuchsia-300' : 'text-muted-foreground border-border'}`}
                data-testid="lookahead-filter-linked">Has 3D links</button>
        <Input value={discipline} onChange={e => setDiscipline(e.target.value)}
               placeholder="Discipline codes (e.g. PIP,STR)"
               className="h-6 w-52 text-[11px]" data-testid="lookahead-filter-discipline" />
        <Input value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Find activity…" className="h-6 w-44 text-[11px]"
               data-testid="lookahead-search" />
        <button onClick={() => setTreeMode(v => !v)}
                className={`ml-auto px-2 py-1 rounded-full border ${treeMode ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-500/20 dark:text-emerald-300' : 'text-muted-foreground border-border'}`}
                data-testid="lookahead-filter-tree">{treeMode ? 'Tree' : 'Flat'}</button>
      </div>

      {/* Bucket cards */}
      <div className="grid grid-cols-4 gap-2 px-3 py-2">
        {BUCKETS.map(b => (
          <button key={b.key}
                  onClick={() => setActiveBucket(a => (a === b.key ? null : b.key))}
                  className={`rounded-lg border px-3 py-2 text-left transition-colors ${
                    activeBucket === b.key ? 'border-sky-500 ring-1 ring-sky-500/40' : 'border-border hover:border-muted-foreground/40'}`}
                  data-testid={`lookahead-bucket-${b.key}`}>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm" style={{ background: b.color }} />
              <span className="text-[10px] tracking-wide text-muted-foreground">{b.label}</span>
            </div>
            <div className={`text-2xl font-semibold ${b.text}`}>{counts[b.key] ?? 0}</div>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 border-t">
        <div className="px-3 py-1.5 text-[10px] tracking-wide text-muted-foreground border-b">
          {rendered.length} ROWS — {activeBucket ? activeBucket.replace('_', ' ') : 'ALL BUCKETS'} · {treeMode ? 'TREE' : 'FLAT'}
          {selectedCode && <span className="ml-2 text-sky-500">· {selectedCode} isolated in 3D</span>}
        </div>
        <ScrollArea className="h-[calc(100%-26px)]">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-muted/60 backdrop-blur">
              <tr className="text-[10px] tracking-wide text-muted-foreground">
                <th className="text-left font-medium px-3 py-1.5 w-40">WBS</th>
                <th className="text-left font-medium px-2 py-1.5">Task</th>
                <th className="text-right font-medium px-2 py-1.5 w-24">B Start</th>
                <th className="text-right font-medium px-2 py-1.5 w-24">B Finish</th>
                <th className="text-right font-medium px-2 py-1.5 w-24">F Start</th>
                <th className="text-right font-medium px-2 py-1.5 w-24">F Finish</th>
                <th className="text-right font-medium px-2 py-1.5 w-24">A Start</th>
                <th className="text-right font-medium px-2 py-1.5 w-24">A Finish</th>
                <th className="text-right font-medium px-2 py-1.5 w-16">% Done</th>
                <th className="text-right font-medium px-2 py-1.5 w-20">Objects</th>
                <th className="text-left font-medium px-2 py-1.5 w-28">Flags</th>
              </tr>
            </thead>
            <tbody>
              {rendered.map(r => (
                <tr key={r.activityCode}
                    onClick={() => r.bucket && pickRow(r)}
                    className={`border-b border-border/40 ${r.bucket ? 'cursor-pointer hover:bg-muted/50' : 'bg-muted/20'} ${
                      selectedCode === r.activityCode ? 'bg-sky-500/10' : ''}`}
                    data-testid={`lookahead-row-${r.activityCode}`}>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-muted-foreground">
                    <span style={{ paddingLeft: `${(r._level || 0) * 12}px` }} className="inline-flex items-center gap-1">
                      {r._hasChildren ? (
                        <button onClick={(e) => { e.stopPropagation(); toggle(r.activityCode); }}
                                data-testid={`lookahead-toggle-${r.activityCode}`}>
                          {collapsed.has(r.activityCode)
                            ? <ChevronRight className="w-3 h-3" />
                            : <ChevronDown className="w-3 h-3" />}
                        </button>
                      ) : <span className="w-3 inline-block" />}
                      {r.activityCode}
                    </span>
                  </td>
                  <td className="px-2 py-1.5">
                    <span className={r.isSummary || !r.bucket ? 'font-medium' : ''}>
                      {r.activityName || '—'}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{d10(r.bStart)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{d10(r.bFinish)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{d10(r.fStart)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{d10(r.fFinish)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{d10(r.aStart)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{d10(r.aFinish)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {r.pctDone == null ? '—' : `${r.pctDone}%`}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {r.objectCount ? (
                      <span className="inline-flex items-center gap-1 text-fuchsia-500">
                        <Layers className="w-3 h-3" />{r.objectCount}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center gap-1">
                      {r.bucket && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-medium text-white"
                              style={{ background: r.color }}>
                          {r.bucket.replace('_', ' ')}
                        </span>
                      )}
                      {r.isCriticalPath && <Badge variant="destructive" className="text-[9px] px-1 py-0">CP</Badge>}
                      {r.slipDays > 0 && (
                        <span className="text-[9px] text-red-500">+{r.slipDays}d</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!rendered.length && !loading && (
                <tr><td colSpan={11} className="px-3 py-8 text-center text-muted-foreground">
                  Nothing falls inside this horizon — widen the window or clear the filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </ScrollArea>
      </div>
    </div>
  );
}

export default FourDLookAhead;
